import { markTenantSwitchStart } from '@/components/layout/TenantSwitchOverlay';
import {
  confirmPasswordReset,
  getMe,
  listSessions,
  login,
  logout,
  requestPasswordReset,
  resendVerificationEmail,
  revokeSession,
  signup,
  switchTenant,
  verifyEmail,
} from '@/lib/api/auth';
import { ApiError } from '@/lib/api/client';
import type {
  ConfirmPasswordResetInput,
  LoginInput,
  RequestPasswordResetInput,
  SignupInput,
  VerifyEmailInput,
} from '@disparos/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const KEYS = {
  all: ['auth'] as const,
  me: () => [...KEYS.all, 'me'] as const,
  sessions: () => [...KEYS.all, 'sessions'] as const,
};

export function useMe() {
  return useQuery({
    queryKey: KEYS.me(),
    queryFn: ({ signal }) => getMe(signal),
    // Não retentar em 401 — usuário não está logado, não vamos forçar.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
    // Stale time longo — uma vez logado, sessão dura. Refresh em janela nova.
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignupInput) => signup(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => switchTenant(tenantId),
    onSuccess: (data) => {
      // Marca o switch em sessionStorage pra `TenantSwitchOverlay` mostrar
      // feedback visual contínuo (cobre a tela branca do reload). 1000ms
      // pré-reload dá tempo da entrada do card (1200ms) chegar próximo
      // ao final antes do reload disparar — sensação de continuidade.
      qc.clear();
      markTenantSwitchStart(data.tenant.id, data.tenant.name);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Falha ao trocar tenant';
      toast.error(msg);
    },
  });
}

export function useSessions() {
  return useQuery({
    queryKey: KEYS.sessions(),
    queryFn: ({ signal }) => listSessions(signal),
    staleTime: 30_000,
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.sessions() });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Falha ao revogar sessão');
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: RequestPasswordResetInput) => requestPasswordReset(input),
    // Sem onError de toast — anti-enumeration: UI sempre mostra sucesso
    // independente do response (página /forgot-password trata setSubmitted).
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (input: ConfirmPasswordResetInput) => confirmPasswordReset(input),
    onError: (err) => {
      // toast só pra erros não-400 (400 vira mensagem inline na página).
      if (err instanceof ApiError && err.status === 400) return;
      toast.error(err instanceof Error ? err.message : 'Falha ao redefinir senha');
    },
  });
}

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VerifyEmailInput) => verifyEmail(input),
    onSuccess: () => {
      // Invalida `me` pra UI re-renderizar sem o banner.
      void qc.invalidateQueries({ queryKey: KEYS.me() });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) return;
      toast.error(err instanceof Error ? err.message : 'Falha ao verificar email');
    },
  });
}

export function useResendVerificationEmail() {
  return useMutation({
    mutationFn: () => resendVerificationEmail(),
    onSuccess: () => {
      toast.success('Email de verificação reenviado. Confira sua caixa de entrada.');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Falha ao reenviar email');
    },
  });
}

export const authKeys = KEYS;
